/* eslint-disable react-hooks/exhaustive-deps --
   File grande con pattern fetch-then-setState e mutation di useRef.current
   ampiamente usati (cache slide, generationToken, blob URLs). Refactor con
   useTransition + immer fuori scope di questa iter UI mobile. */
import React, { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowsClockwise, Crown, Lock,
  ImageSquare, FilmSlate, Sparkle, Stack, ArrowsClockwise as ArrowsClockwiseDup,
  House, Calendar, Tag, CheckCircle, CurrencyEur, DownloadSimple,
} from "@phosphor-icons/react";
import BrandSpinner from "@/components/BrandSpinner";
import DismissibleIntro from "@/components/DismissibleIntro";
import SectionCoachmark from "@/components/SectionCoachmark";
import PackBox from "@/components/social/PackBox";
import StaticStyleSelector from "@/components/social/StaticStyleSelector";
import FormatSelector from "@/components/social/FormatSelector";
import { usePackJobs } from "@/components/social/usePackJobs";
import usePropertyEnhanceStatus from "@/hooks/usePropertyEnhanceStatus";
import { exportStaticAsPng, exportBrochureAsPdf, exportNodesAsZip, EXPORT_WIDTHS, isIOSDevice, smartDownloadBlob } from "@/lib/staticExport";
import { generateVectorBrochure } from "@/lib/brochureVectorPDF";
import { LAYOUTS, COLORS } from "@/components/StaticTemplatePreview";
import { buildData } from "@/components/staticTemplate/shared";
import { resolveManifestProperty, resolveManifestAgency } from "@/lib/manifestUrls";
import { makeAssetFilename } from "@/lib/assetNaming";
import { archiveAsset } from "@/lib/socialArchive";

import QuickGuide from "@/components/social/QuickGuide";
import PreviewBody from "@/components/social/PreviewBody";
import SocialActions from "@/components/social/SocialActions";
import CaptionGenerator from "@/components/social/CaptionGenerator";
import {
  STATIC_FORMATS,
  STATIC_LAYOUT_OPTIONS,
  STATIC_COLOR_OPTIONS,
  LAYOUT_MINI,
  classifyKind,
  labelForFormat,
} from "@/components/social/socialConstants";

/**
 * SocialStudioTab — per ogni immobile, contenuti social pronti.
 *
 * Backend endpoints (vedi routes/social_studio.py):
 *   GET /api/properties/{id}/social/manifest             → metadata + plan
 *   GET /api/properties/{id}/social/post.png             → 1080×1080 PNG
 *   GET /api/properties/{id}/social/reel.mp4             → 1080×1920 MP4 6s
 *   GET /api/properties/{id}/social/before-after.mp4     → 1080×1920 MP4 5s
 *   GET /api/properties/{id}/social/carousel.zip         → 5 PNG zip
 *   GET /api/properties/{id}/social/carousel/{idx}.png   → singola slide
 *   GET /api/story/today.png                             → 1080×1920 PNG
 *
 * Watermark "P + prontoimmo.it" è applicato server-side per i piani non-Pro.
 *
 * Iter 274: refactor. Estratti in /components/social/:
 *   - socialConstants.js (STATIC_FORMATS, LAYOUT/COLOR options, helpers)
 *   - QuickGuide.jsx (4 card scenariate in cima)
 *   - PreviewBody.jsx (anteprima + aspect toggle + before/after controls)
 *   - SocialActions.jsx (bottoni Condividi/Scarica/WhatsApp/Link pubblico)
 */
export default function SocialStudioTab({ propertyId }) {
  const [manifest, setManifest] = useState(null);
  // iter v33 r18 · Blocco preventivo: se le foto stanno ancora migliorando
  // sul backend, i pulsanti Genera vengono disabilitati per evitare che
  // ffmpeg/PIL girino sotto pressione RAM e diano 503.
  const enhanceStatus = usePropertyEnhanceStatus(propertyId);
  const enhanceBusy = enhanceStatus.enhanceInProgress;
  const [active, setActive] = useState("post"); // post | reel | story | before_after | carousel
  // Layout + colore separati per i formati STATICI (FASE 1a v2)
  const [staticLayout, setStaticLayout] = useState("moderno");
  const [staticColor, setStaticColor] = useState("verde_brera");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  // iter v33 r54 · User M1978: dopo il toast rosso "Non riesco a comporre
  // il reel..." la UI restava congelata su "Sto creando il reel..." con
  // lo spinner attivo perche' il render era `loading || !previewUrl`.
  // Nuovo stato esplicito per mostrare una card di errore actionable
  // (messaggio + Riprova) al posto dello spinner.
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Carousel: keep N slide previews and an active index
  const [carouselSlides, setCarouselSlides] = useState([]); // array of { idx, blobUrl }
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [carouselZip, setCarouselZip] = useState(null);
  const objectUrlRef = useRef(null);
  const carouselUrlsRef = useRef([]);
  // iter v28 r46 — Cache delle anteprime: Map<url_key, {blobUrl, slides?}>
  // Quando l'utente torna su una combinazione layout/colore/aspect/foto
  // già vista, riusiamo il blob senza richiamare il backend (era ~3-8s di
  // attesa per il rendering server-side). Per il Carosello memorizziamo
  // l'array di slide e lo zip blob. I blobUrl vengono revocati solo allo
  // smontaggio del component → vita = sessione editor.
  const previewCacheRef = useRef(new Map()); // key → { blobUrl } | { slides, zipUrl }
  const staticPreviewRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [aiSelecting, setAiSelecting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const styleSavedRef = useRef({ layout: null, color: null });
  const [styleSyncing, setStyleSyncing] = useState(false);
  const [stylePulse, setStylePulse] = useState(0);
  // iter v33 r99f · User: «metti il tempo che ci impiega, quanto manca alla
  // fine della creazione del reel così uno sa che funziona». State del job
  // reel con elapsed/eta live per mostrare la banda di progresso.
  const [reelJobProgress, setReelJobProgress] = useState(null);
  // { jobId, elapsed, etaRemaining, status } · null quando non ci sono job attivi
  // Lista delle share pubbliche attive per questo immobile (asset_type='reel'|'before_after')
  const [shares, setShares] = useState([]);
  // Etichette del Prima/Dopo
  const [baLabelKind, setBaLabelKind] = useState("prima_dopo");
  // iter v26: photo_id selezionato per Prima/Dopo (null = pick automatico)
  // iter v28 r143 — Multi-stanza (max 4): baPhotoIds è ora un array ordinato.
  // Manteniamo baPhotoId per retrocompatibilità (= primo elemento dell'array).
  const [baPhotoIds, setBaPhotoIds] = useState([]);
  const baPhotoId = baPhotoIds[0] || null;
  const setBaPhotoId = (id) => setBaPhotoIds(id ? [id] : []);
  // iter v27 r11 — pattern 2-step iOS per brochure A4.
  // Su iOS lo `pdf.save()` di jsPDF dopo i 5-15s di generazione fallisce
  // perché il gesture utente è scaduto e Safari blocca tutto. Pre-generiamo
  // il blob e mostriamo il bottone verde "Salva su File · Condividi" che
  // l'utente tocca con un gesture FRESCO → share-sheet nativo iOS pulito.
  // Reset quando cambia formato / layout / colore / proprietà.
  const [brochurePdfReady, setBrochurePdfReady] = useState(null); // {blob, filename} | null
  useEffect(() => { setBrochurePdfReady(null); }, [active, staticLayout, staticColor, propertyId]);

  // iter v27: photo_id "hero" selezionato per Post/Reel/Story.
  //   null  → backend usa la scelta automatica (smart ranking AI).
  //   <id>  → l'agente ha scelto manualmente quale foto usare come copertina.
  const [heroPhotoId, setHeroPhotoId] = useState(null);
  // Aspect ratio per Post / Prima-Dopo / Reel
  const [postAspect, setPostAspect] = useState("1x1");
  const [baAspect, setBaAspect] = useState("9x16");
  const [reelAspect, setReelAspect] = useState("9x16");
  // Toggle GLOBALE "Mostra prezzo" — vale per Post, Story, Reel, Occasione,
  // Prima/Dopo. iter 313 r21 v21 — user feedback: di default niente prezzo,
  // l'agente decide formato per formato.
  const [showPrice, setShowPrice] = useState(false);
  // "Pacchetto Social completo" download — 5 file in 1 zip
  const [packBusy, setPackBusy] = useState(false);
  // Tono didascalia: editoriale | amichevole | urgente | professionale
  const [captionTone, setCaptionTone] = useState("editoriale");
  // A/B test didascalie (1, 2, 3 varianti).
  const [captionVariants, setCaptionVariants] = useState(1);

  // iter 313 r21 v19 — "Per occasione" unificato: Open House + Venduto.
  // Riusano lo stesso composer Python (`/social/post.png?occasion=...`)
  // del Post 1:1 / Story 9:16 standard, con badge in alto e foto oscurata.
  const [occasionFormat, setOccasionFormat] = useState("1x1"); // 1x1 | 9x16
  const [occasionDate, setOccasionDate] = useState(""); // YYYY-MM-DD
  const [occasionTime, setOccasionTime] = useState(""); // HH:MM
  const [occasionTimeEnd, setOccasionTimeEnd] = useState(""); // HH:MM
  const [occasionDays, setOccasionDays] = useState(14);
  // iter v28 r98 — Occasione "Prezzo nuovo": nuovo prezzo da mostrare
  const [occasionNewPrice, setOccasionNewPrice] = useState("");

  // Background pack jobs (programma per dopo) + lista attiva per anti-dup
  const { enqueue: enqueuePackJob, jobs: activePackJobs } = usePackJobs();

  /** Builda il body comune ai 2 endpoint (sync `pack.zip` e async `pack/start`). */
  const _buildPackBody = () => {
    const body = {
      // iter v29 r214 — Fallback "elegante" (coerente backend) invece di
      // "moderno" che era arbitrario e causava: «vedevo elegante ma il
      // pack scaricava moderno».
      layout: staticLayout || "elegante",
      label_kind: baLabelKind,
      caption_tone: captionTone,
      caption_variants: captionVariants || 1,
      // iter v29 r215 — User M1535: «nel pacchetto social devono esserci
      // TUTTI i materiali tranne quelli per occasione». Reel ora di default
      // INCLUSO (era opt-in). Story 9:16 e Carosello sono aggiunti
      // automaticamente lato backend (_generate_pack_bytes).
      include_reel: true,
    };
    if (staticColor) body.template = staticColor;
    return body;
  };

  const [manifestError, setManifestError] = useState(null);

  const fetchManifest = async () => {
    try {
      setManifestError(null);
      const { data } = await api.get(`/properties/${propertyId}/social/manifest`);
      setManifest(data);
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const errMsg = code === 401
        ? "Sessione scaduta. Effettua il login di nuovo."
        : code === 404
          ? "Immobile non trovato."
          : detail || "Connessione lenta · ho avuto problemi a caricare l'Editor social.";
      setManifestError(errMsg);
      toast.error(errMsg);
    }
  };

  /** Carica la lista delle share attive (non revocate, non scadute) per questo immobile. */
  const fetchShares = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/social/shares`);
      setShares(Array.isArray(data) ? data : []);
    } catch (err) {
      setShares([]);
    }
  };

  /** Revoca una share pubblica e aggiorna la lista. */
  const onRevokeShare = async (shareId) => {
    if (!shareId) return;
    if (!window.confirm("Revocare questo link? Diventerà inutilizzabile per chi l'ha già ricevuto.")) return;
    try {
      await api.delete(`/social/share/${shareId}`);
      toast.success("Link revocato.");
      fetchShares();
    } catch (err) {
      toast.error("Non sono riuscito a revocare il link.");
    }
  };

  /** Copia un URL pubblico negli appunti. */
  const copyShareUrl = async (relativeOrFullUrl) => {
    const fullUrl = relativeOrFullUrl?.startsWith("http")
      ? relativeOrFullUrl
      : `${BACKEND_URL}${relativeOrFullUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copiato.");
    } catch {
      toast.success(`Link: ${fullUrl}`);
    }
  };

  const _revokeAll = () => {
    // iter v28 r46 — Revoca solo quando smontiamo il component o cambia
    // propertyId (vedi useEffect dedicato). Durante la navigazione
    // tra formati, i blob restano nella cache e vengono riusati al volo.
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    carouselUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    carouselUrlsRef.current = [];
    // Anche la cache (i blob sono dentro carouselUrlsRef o objectUrlRef
    // ma per sicurezza la svuotiamo)
    previewCacheRef.current.clear();
  };

  const fetchAsset = async (key, opts = {}) => {
    // iter r95e · Fix 3 · opts.forceRegen=true bypassa cache e forza
    // rigenerazione con nuovo addebito. Usato dal pulsante "Rigenera Reel".
    const forceRegen = !!opts.forceRegen;
    // iter v33 r18 · Blocco preventivo. Se l'enhance foto è in corso,
    // ffmpeg/PIL girano sotto pressione RAM e possono dare 503. Blocco
    // qui con toast informativo — l'utente riprova quando finisce.
    if (enhanceBusy) {
      toast.info("Le foto stanno ancora migliorando", {
        description: `Aspetta ~${enhanceStatus.remaining || 1} foto e riprova (in media 60-90s).`,
        duration: 4500,
      });
      return;
    }
    // iter v28 r46 — Costruzione URL deterministico PRIMA del fetch così
    // possiamo usarlo come cache-key. Tutti i parametri che influenzano il
    // rendering sono già nella querystring.
    let cacheKey = null;
    let urlForKey = null;
    if (key === "carousel") {
      cacheKey = `carousel?template=${staticColor}&layout=${staticLayout}&pid=${propertyId}`;
    } else if (key === "story") {
      const sp = showPrice ? "true" : "false";
      // iter v28 r48 — Story ora scoped all'annuncio + accetta photo_id +
      // applica template colore come il Post (era endpoint generico).
      // iter v28 r105 — Aggiunto `layout` così la Story rispetta la scelta
      // Elegante/Luxury/Moderno/Minimal (prima ignorata, default elegante).
      const storyParams = new URLSearchParams({
        show_price: sp,
        property_id: propertyId,
        template: staticColor,
        layout: staticLayout,
      });
      if (heroPhotoId) storyParams.set("photo_id", heroPhotoId);
      cacheKey = `story?${storyParams.toString()}&pid=${propertyId}`;
      urlForKey = `/story/today.png?${storyParams.toString()}`;
    } else if (key === "post") {
      const sp = showPrice ? "true" : "false";
      const postParams = new URLSearchParams({
        template: staticColor, aspect: postAspect, layout: staticLayout, show_price: sp,
      });
      if (heroPhotoId) postParams.set("photo_id", heroPhotoId);
      cacheKey = `post?${postParams.toString()}&pid=${propertyId}`;
      urlForKey = `/properties/${propertyId}/social/post.png?${postParams.toString()}`;
    } else if (key === "occ_open_house" || key === "occ_venduto" || key === "occ_prezzo_nuovo") {
      const sp = showPrice ? "true" : "false";
      const occMap = { occ_open_house: "open_house", occ_venduto: "venduto", occ_prezzo_nuovo: "prezzo_nuovo" };
      const params = new URLSearchParams({
        template: staticColor, aspect: occasionFormat, layout: staticLayout,
        occasion: occMap[key],
        show_price: sp,
      });
      if (key === "occ_open_house") {
        if (occasionDate) {
          const [, mm, dd] = occasionDate.split("-");
          if (dd && mm) params.set("event_date", `${dd}/${mm}`);
        }
        if (occasionTime) params.set("event_time", occasionTime);
        if (occasionTimeEnd) params.set("event_time_end", occasionTimeEnd);
      } else if (key === "occ_venduto") {
        const n = Number(occasionDays);
        if (Number.isFinite(n) && n > 0) params.set("days_to_sell", String(Math.round(n)));
      } else if (key === "occ_prezzo_nuovo") {
        const n = Number(occasionNewPrice);
        if (Number.isFinite(n) && n > 0) params.set("new_price", String(Math.round(n)));
      }
      cacheKey = `${key}?${params.toString()}&pid=${propertyId}`;
      urlForKey = `/properties/${propertyId}/social/post.png?${params.toString()}`;
    } else if (key === "before_after") {
      const sp = showPrice ? "true" : "false";
      const baParams = new URLSearchParams({
        template: staticColor, layout: staticLayout, label_kind: baLabelKind,
        aspect: baAspect, show_price: sp,
      });
      if (baPhotoIds && baPhotoIds.length > 0) {
        // iter v28 r143 — Multi-stanza: passa CSV photo_ids invece di singolo photo_id
        baParams.set("photo_ids", baPhotoIds.join(","));
      } else if (baPhotoId) {
        baParams.set("photo_id", baPhotoId);
      }
      cacheKey = `before_after?${baParams.toString()}&pid=${propertyId}`;
      urlForKey = `/properties/${propertyId}/social/before-after.mp4?${baParams.toString()}`;
    } else if (key === "reel") {
      const sp = showPrice ? "true" : "false";
      const reelParams = new URLSearchParams({
        template: staticColor, layout: staticLayout, aspect: reelAspect, show_price: sp,
      });
      if (heroPhotoId) reelParams.set("photo_id", heroPhotoId);
      cacheKey = `reel?${reelParams.toString()}&pid=${propertyId}`;
      urlForKey = `/properties/${propertyId}/social/reel.mp4?${reelParams.toString()}`;
    }

    // ─── Cache hit? Mostra subito ───────────────────────────────────────
    // iter r95e · Fix 3 · se forceRegen=true saltiamo il cache client-side
    // (previewCacheRef) altrimenti l'utente rivedrebbe il blob vecchio.
    if (!forceRegen && cacheKey && previewCacheRef.current.has(cacheKey)) {
      const cached = previewCacheRef.current.get(cacheKey);
      if (key === "carousel" && cached.slides) {
        setCarouselSlides(cached.slides);
        setCarouselZip(cached.zipUrl || null);
        setCarouselIdx(0);
        setPreviewUrl(null);
        setLoading(false);
        return;
      } else if (cached.blobUrl) {
        setPreviewUrl(cached.blobUrl);
        setCarouselSlides([]);
        setCarouselZip(null);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setPreviewError(null);
    setPreviewUrl(null);
    setCarouselSlides([]);
    setCarouselZip(null);
    setCarouselIdx(0);
    try {
      if (key === "carousel") {
        const slidesCount = manifest?.formats?.carousel?.slides || 5;
        // iter v29 r211 — User M1531: «non mi carica il carosello,
        // generazione fallita, lento in 4G». Causa: 5 richieste in
        // PARALLELO che arrivavano TUTTE prima della cache backend →
        // 5 build PIL simultanei + 5x download foto + ingress timeout.
        // Fix: prima slide sequenziale (popola la cache _carousel_cache),
        // poi 4 in parallelo (servite O(1) dalla cache). Timeout esplicito
        // 60s per la prima, 30s per le successive.
        const firstResp = await api.get(
          `/properties/${propertyId}/social/carousel/1.png?template=${staticColor}&layout=${staticLayout}`,
          { responseType: "blob", timeout: 60000 }
        );
        const remainingReqs = Array.from({ length: slidesCount - 1 }, (_, i) =>
          api.get(
            `/properties/${propertyId}/social/carousel/${i + 2}.png?template=${staticColor}&layout=${staticLayout}`,
            { responseType: "blob", timeout: 30000 }
          )
        );
        const remainingResps = await Promise.all(remainingReqs);
        const slides = [firstResp, ...remainingResps].map((r, i) => {
          const blobUrl = URL.createObjectURL(r.data);
          carouselUrlsRef.current.push(blobUrl);
          return { idx: i + 1, blobUrl };
        });
        setCarouselSlides(slides);
        // Cache delle slide
        if (cacheKey) previewCacheRef.current.set(cacheKey, { slides, zipUrl: null });
        api.get(`/properties/${propertyId}/social/carousel.zip?template=${staticColor}&layout=${staticLayout}`, { responseType: "blob", timeout: 60000 })
          .then((r) => {
            const zipUrl = URL.createObjectURL(r.data);
            carouselUrlsRef.current.push(zipUrl);
            setCarouselZip(zipUrl);
            // Aggiungi zipUrl alla cache
            if (cacheKey && previewCacheRef.current.has(cacheKey)) {
              const entry = previewCacheRef.current.get(cacheKey);
              previewCacheRef.current.set(cacheKey, { ...entry, zipUrl });
            }
          })
          .catch(() => { /* non-blocking */ });
      } else {
        // iter v33 r37 · User M1770: «il reel di nuovo non si carica».
        // Root cause: la GET sync /reel.mp4 impiega 45-90s ma Cloudflare
        // (proxy prod prontoimmo.it) taglia dopo 30s → 502/524 casuale.
        // Fix: uso lo stesso pattern async del V2 (POST /reel/start →
        // polling /reel-jobs/{id} ogni 2s → GET /reel.mp4 con cache hit).
        // Cloudflare non taglia più perché ogni request è < 5s.
        const isVideo = key === "reel" || key === "before_after";
        if (key === "reel") {
          const startUrl = urlForKey.replace("/reel.mp4?", "/reel/start?")
            + (forceRegen ? "&force_regen=1" : "");
          // iter v33 r99f · sessionStorage key per riagganciare il job dopo standby
          const sessionKey = `reel_active_job_${propertyId || 'unknown'}`;

          const pollAndUpdateProgress = async (jobId, maxAttempts = 60) => {
            let consecutiveTimeouts = 0;
            for (let i = 0; i < maxAttempts; i++) {
              await new Promise((r) => setTimeout(r, 2000));
              let sResp;
              try {
                sResp = await api.get(`/social/reel-jobs/${jobId}`, {
                  timeout: 20000, validateStatus: () => true,
                });
                consecutiveTimeouts = 0;
              } catch (pollErr) {
                consecutiveTimeouts += 1;
                if (consecutiveTimeouts >= 3) {
                  throw new Error("Rete instabile. Il reel sta ancora venendo generato, riprova fra 30 secondi.");
                }
                continue;
              }
              const s = sResp.data || {};
              if (sResp.status === 404) {
                throw Object.assign(new Error("restart"), { _restart: true });
              }
              // iter v33 r99f · aggiorno la banda live con elapsed + eta
              setReelJobProgress({
                jobId: jobId,
                elapsed: s.elapsed_seconds ?? i * 2,
                etaRemaining: s.eta_remaining ?? Math.max(0, 45 - i * 2),
                status: s.status || "running",
              });
              if (s.status === "ready") {
                sessionStorage.removeItem(sessionKey);
                setReelJobProgress(null);
                return;
              }
              if (s.status === "failed") {
                sessionStorage.removeItem(sessionKey);
                setReelJobProgress(null);
                const detailMsg = s.error_detail || "Generazione reel non riuscita.";
                throw new Error(detailMsg);
              }
            }
            sessionStorage.removeItem(sessionKey);
            setReelJobProgress(null);
            throw new Error("Sta impiegando troppo tempo. Riprova fra un minuto.");
          };

          const runJobWithPolling = async () => {
            // iter v33 r99f · Riaggancio job già in corso se presente
            // in sessionStorage (utile quando l'utente ha messo l'app in
            // background e il timer JS iOS si è pausato).
            const existingJobId = sessionStorage.getItem(sessionKey);
            if (existingJobId) {
              try {
                const chk = await api.get(`/social/reel-jobs/${existingJobId}`, {
                  timeout: 8000, validateStatus: () => true,
                });
                if (chk.status === 200) {
                  const cs = chk.data || {};
                  if (cs.status === "running") {
                    // Job esistente ancora in corso → riaggancio
                    setReelJobProgress({
                      jobId: existingJobId,
                      elapsed: cs.elapsed_seconds ?? 0,
                      etaRemaining: cs.eta_remaining ?? 45,
                      status: "running",
                    });
                    return await pollAndUpdateProgress(existingJobId);
                  }
                  if (cs.status === "ready") {
                    // Job già completato → salta direttamente al download
                    sessionStorage.removeItem(sessionKey);
                    return;
                  }
                }
              } catch { /* fallthrough: creo nuovo job */ }
              sessionStorage.removeItem(sessionKey);
            }
            const startResp = await api.post(startUrl, null, { timeout: 15000 });
            const sd = startResp.data || {};
            if (sd.status !== "ready" && sd.status !== "running") {
              throw new Error("Generazione reel non avviata. Riprova fra poco.");
            }
            if (!sd.job_id) return; // cache hit immediato
            sessionStorage.setItem(sessionKey, sd.job_id);
            setReelJobProgress({
              jobId: sd.job_id,
              elapsed: 0,
              etaRemaining: sd.eta_seconds ?? 45,
              status: "running",
            });
            await pollAndUpdateProgress(sd.job_id);
          };
          try {
            await runJobWithPolling();
          } catch (e) {
            if (e?._restart) {
              // Job perso (backend riavviato): ritenta 1 volta
              await runJobWithPolling();
            } else {
              throw e;
            }
          }
        }
        // Ora scarichiamo il .mp4 (cache hit → 0s)
        const resp = await api.get(urlForKey, {
          responseType: "blob",
          timeout: isVideo ? 60000 : 60000,
        });
        const blobUrl = URL.createObjectURL(resp.data);
        // iter v28 r46 — Tutti i blob (single + carousel) sono tracciati
        // in carouselUrlsRef per la pulizia finale; in più sono in cache
        // tramite previewCacheRef. NON usiamo più objectUrlRef per evitare
        // di revocare il blob precedente quando ne arriva uno nuovo —
        // serve per il riuso istantaneo da cache.
        carouselUrlsRef.current.push(blobUrl);
        setPreviewUrl(blobUrl);
        // Cache del singolo blob
        if (cacheKey) previewCacheRef.current.set(cacheKey, { blobUrl });
      }
    } catch (err) {
      // iter v28 r448 — Log dettagliato per facilitare il debug
      console.error("[social-studio] preview generation failed:", {
        key, url: urlForKey, status: err?.response?.status,
        detail: err?.response?.data?.detail || err?.message,
      });
      // iter v33 r37 · User M1770: mostra "Request failed with status code 503"
      // invece del messaggio italiano che il backend rimanda. Causa: quando
      // responseType: "blob", il body dell'errore arriva come Blob → il check
      // precedente `err?.response?.data?.detail` era undefined e cadeva su
      // err.message. Ora tento SEMPRE prima di decodificare il blob.
      let detail = null;
      const rawBody = err?.response?.data;
      if (rawBody instanceof Blob) {
        try {
          const text = await rawBody.text();
          const parsed = JSON.parse(text);
          detail = parsed?.detail || parsed?.error || null;
        } catch { /* body non JSON */ }
      } else if (rawBody && typeof rawBody === "object") {
        detail = rawBody.detail || rawBody.error || null;
      }
      if (!detail) detail = err?.message;
      // iter v33 r49 · User: «SISTEMA STO CAZZO DI REEL». Anche dopo r48
      // se un qualsiasi timeout axios sfugge (per esempio la /reel.mp4
      // finale dopo che il job e' ready), non deve MAI piu' apparire
      // "Generazione fallita: timeout of Xms exceeded" all'utente. Ora
      // qualsiasi stringa contenente "timeout" viene tradotta in un
      // messaggio morbido, informativo e in italiano.
      const isTimeoutMessage = typeof detail === "string"
        && (detail.toLowerCase().includes("timeout") || err?.code === "ECONNABORTED");
      const status = err?.response?.status;
      let errorMsg;
      if (isTimeoutMessage) {
        errorMsg = "Il reel sta impiegando piu' del previsto. Se attendi ancora qualche secondo e riprovi, dovrebbe apparire (il server lo sta ancora generando).";
      } else if (status === 503) {
        // Se detail è ancora il generico axios "Request failed…", usa il
        // fallback italiano invece di mostrare l'errore inglese all'utente.
        const isGenericAxios = typeof detail === "string" && detail.startsWith("Request failed with status");
        errorMsg = (isGenericAxios || !detail)
          ? "Il server sta lavorando su altri contenuti. Riprova fra 30 secondi."
          : detail;
      } else if (detail && !detail.startsWith?.("Request failed with status")) {
        errorMsg = `Generazione fallita: ${detail}`;
      } else if (err?.code === "ECONNABORTED") {
        errorMsg = "Generazione troppo lenta. Riprova fra poco.";
      } else {
        errorMsg = "Generazione fallita. Riprova fra poco.";
      }
      toast[isTimeoutMessage || status === 503 ? "info" : "error"](errorMsg);
      // iter v33 r54 · Settiamo l'errore così il render non resta bloccato
      // su "Sto creando il reel..." (loading=false ma previewUrl=null).
      // In PreviewBody appare una card actionable con Riprova.
      setPreviewError(errorMsg);
    } finally {
      setLoading(false);
      // iter v33 r99f · Cleanup del banner progress reel se ancora attivo
      // (es. dopo un errore o cache-hit del blob)
      setReelJobProgress(null);
    }
  };

  useEffect(() => {
    fetchManifest();
    fetchShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // iter v33 r99f · User: «l'app in standby mi sembra che ogni volta si
  // ricarichi». Root cause: iOS pausa i timer JavaScript (setTimeout,
  // fetch, ecc.) quando l'app va in background. Al ritorno in foreground,
  // il polling del reel non riprende → utente vede spinner senza progresso.
  // Fix: quando l'app torna visibile mentre siamo su tab "reel" con job
  // attivo in sessionStorage, ri-lanciamo fetchAsset("reel") che ora ha
  // logica di riaggancio (vede il job_id salvato e ripesca lo stato).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (active !== "reel") return;
      const sessionKey = `reel_active_job_${propertyId || 'unknown'}`;
      const existing = sessionStorage.getItem(sessionKey);
      if (existing && loading) {
        // Job attivo salvato + siamo ancora in fase di loading → riprova
        // il fetchAsset che riaggancerà il job via il pollAndUpdateProgress
        // (esistente in runJobWithPolling).
        fetchAsset("reel");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, propertyId, loading]);

  // Hydrate la scelta layout/colore dal manifest se l'agente l'aveva già salvata
  useEffect(() => {
    const saved = manifest?.social_studio_style;
    if (saved?.layout && saved?.color) {
      setStaticLayout(saved.layout);
      setStaticColor(saved.color);
      styleSavedRef.current = { layout: saved.layout, color: saved.color };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest?.social_studio_style?.layout, manifest?.social_studio_style?.color]);

  // Salva lo stile scelto (layout + colore) sull'immobile
  // iter 287 — anche su localStorage immediatamente (sync), così se l'utente
  // cambia colore e apre subito la Brochure modal entro i 600ms di debounce,
  // la modal trova comunque la scelta più recente.
  useEffect(() => {
    if (!propertyId) return;
    try {
      localStorage.setItem(
        `pronto:social-style:${propertyId}`,
        JSON.stringify({ layout: staticLayout, color: staticColor, ts: Date.now() })
      );
    } catch { /* private mode / quota */ }
  }, [propertyId, staticLayout, staticColor]);

  useEffect(() => {
    if (!propertyId || !manifest) return;
    if (
      styleSavedRef.current.layout === staticLayout &&
      styleSavedRef.current.color === staticColor
    ) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setStyleSyncing(true);
      try {
        await api.post(`/properties/${propertyId}/social/style`, {
          layout: staticLayout, color: staticColor,
        });
        if (cancelled) return;
        styleSavedRef.current = { layout: staticLayout, color: staticColor };
        setStylePulse(Date.now());
      } catch {
        // silently ignore — l'agente può comunque scaricare i file (frontend-rendered)
      } finally {
        if (!cancelled) setStyleSyncing(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [propertyId, manifest, staticLayout, staticColor]);

  // Auto-carica il ranking cached (se presente)
  useEffect(() => {
    if (!propertyId || !manifest) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/properties/${propertyId}/social/photo-ranking`);
        if (!cancelled && r.data?.status === "done") {
          _applyRanking(r.data);
        }
      } catch {
        // 403 (non Pro) o 404 → silently ignore
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // iter 264: auto-switch della modalità Prima/Dopo se quella corrente non è compatibile
  useEffect(() => {
    if (active !== "before_after" || !manifest?.formats?.before_after) return;
    const f = manifest.formats.before_after;
    if (baLabelKind === "prima_dopo" && !f.has_staged_furnish && f.has_restyle) {
      setBaLabelKind("oggi_domani");
    } else if (baLabelKind === "oggi_domani" && !f.has_restyle && f.has_staged_furnish) {
      setBaLabelKind("prima_dopo");
    }
  }, [active, manifest, baLabelKind]);

  // iter v26: imposta foto default per Prima/Dopo (prima compatibile con il
  // label_kind selezionato). Reset se le foto attualmente selezionate non
  // sono più compatibili col toggle.
  // iter v28 r143 — Multi-stanza: lavoriamo su baPhotoIds (array max 4).
  useEffect(() => {
    if (active !== "before_after" || !manifest?.formats?.before_after) return;
    const photos = manifest.formats.before_after.photos || [];
    if (photos.length === 0) {
      if (baPhotoIds.length > 0) setBaPhotoIds([]);
      return;
    }
    const wantKind = baLabelKind === "prima_dopo" ? "staging" : "restyle";
    const compatible = photos.filter((p) => p.kind === wantKind);
    if (compatible.length === 0) {
      // Nessuna foto del tipo richiesto → svuota (backend fa fallback)
      if (baPhotoIds.length > 0) setBaPhotoIds([]);
      return;
    }
    // Filtra l'array tenendo solo le selezioni ancora compatibili (max 4)
    const compatibleIdSet = new Set(compatible.map((p) => p.photo_id));
    const filtered = baPhotoIds.filter((id) => compatibleIdSet.has(id));
    if (filtered.length === 0) {
      // Riparti dalla prima
      setBaPhotoIds([compatible[0].photo_id]);
    } else if (filtered.length !== baPhotoIds.length) {
      setBaPhotoIds(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, manifest, baLabelKind]);

  useEffect(() => {
    if (STATIC_FORMATS.includes(active)) return;
    // iter 313 r35 — cambiando tab, pulisci subito la preview vecchia +
    // mostra spinner. Prima il previewUrl del formato precedente restava
    // visibile finché il fetch del nuovo formato non finiva (es. cambi
    // da Post a Reel → vedi ancora il post finché reel render).
    setLoading(true);
    setPreviewUrl(null);
    setCarouselSlides([]);
    // iter 313 r21 v19 — i formati "Per occasione" non hanno entry nel manifest
    // (riusano l'endpoint post.png con occasion=). Bypass del check available.
    const isOccasion = active === "occ_open_house" || active === "occ_venduto" || active === "occ_prezzo_nuovo";
    if (isOccasion && manifest) {
      fetchAsset(active);
    } else if (manifest?.formats?.[active]?.available) {
      fetchAsset(active);
    } else if (manifest) {
      // Manifest caricato ma formato non disponibile: stop spinner.
      setLoading(false);
    }
    // iter v28 r46 — Niente più cleanup qui: il revoke svuoterebbe la cache
    // ad ogni cambio formato/layout/colore, vanificando il riuso istantaneo.
    // La pulizia avviene SOLO allo smontaggio (vedi useEffect dedicato).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, manifest?.formats?.[active]?.available, staticColor, staticLayout, baLabelKind, postAspect, baAspect, reelAspect, showPrice, occasionFormat, occasionDate, occasionTime, occasionTimeEnd, occasionDays, occasionNewPrice, heroPhotoId, baPhotoIds]);

  // iter v28 r46 — Cleanup blob URLs SOLO al cambio di propertyId o smontaggio.
  useEffect(() => {
    return () => {
      _revokeAll();
    };
  }, [propertyId]);

  /** Crea (o riusa) un link pubblico al MP4 corrente e lo copia negli appunti. */
  const onCreatePublicLink = async () => {
    if (active !== "reel" && active !== "before_after") return;
    setBusy(true);
    try {
      const resp = await api.post(
        `/properties/${propertyId}/social/share`,
        { asset_type: active === "before_after" ? "before_after" : "reel" },
      );
      const share = resp.data;
      const path = share.public_url || `/api/public/social/${share.share_id}.mp4`;
      const fullUrl = path.startsWith("http") ? path : `${BACKEND_URL}${path}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
        toast.success(`Link copiato! Scade fra 7 giorni · ${share.view_count} viste finora`);
      } catch {
        toast.success(`Link pubblico: ${fullUrl}`);
      }
      fetchShares();
    } catch (err) {
      toast.error("Impossibile generare il link. Riprova fra poco.");
    } finally {
      setBusy(false);
    }
  };

  /** Helper unico per generare il nome del file scaricato.
   *  Usa titolo+città dal manifest così l'agente ritrova i file per
   *  immobile, non per propertyId. */
  const nameFor = (kind, { ext = "png", layout, idx, suffix } = {}) => {
    return makeAssetFilename({
      propertyTitle: manifest?.property?.title,
      propertyCity: manifest?.property?.city,
      kind: kind || active,
      layout: layout ?? staticLayout,
      ext,
      idx,
      suffix,
    });
  };

  /** Condividi su WhatsApp — Growth idea: invia direttamente al cliente. */
  const onWhatsAppShare = async () => {
    const propTitle = manifest?.property?.title || "questo immobile";
    const isVideo = active === "reel" || active === "before_after";
    const isStaticImg = STATIC_FORMATS.includes(active);
    setBusy(true);
    try {
      // iter 313 r21 v12 — Per i VIDEO: scarico MP4 e tento Web Share API
      // con file (mobile iOS/Android). Niente più link pubblico 7gg.
      if (isVideo) {
        if (!previewUrl) {
          toast.error("Anteprima non ancora pronta");
          return;
        }
        try {
          const blob = await (await fetch(previewUrl)).blob();
          const file = new File([blob], nameFor(active, { ext: "mp4" }), { type: "video/mp4" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: propTitle,
              text: `Ciao! Ti mando in anteprima ${propTitle}.`,
            });
            toast.success("Condiviso");
          } else {
            // Desktop fallback: download + apri wa.me con testo
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = file.name;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            const text = `Ciao! Ti mando in anteprima ${propTitle}. (allego il video scaricato)`;
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
            toast.success("Video scaricato · allega su WhatsApp");
          }
        } catch (err) {
          if (err?.name !== "AbortError") toast.error("Condivisione non riuscita");
        }
        return;
      }

      // ─── CASO IMMAGINE STATICA (post, story, brochure, ecc.) ───────
      // Strategia: rendiamo il PNG + tentiamo Web Share API con file
      // (su mobile iOS/Android Chrome/Safari apre il selettore "Condividi
      // su..." dove WhatsApp è una opzione: l'immagine viaggia con il
      // messaggio). Su desktop fallback: auto-download PNG + apri wa.me
      // con testo + toast che spiega di allegare il file scaricato.
      if (isStaticImg && staticPreviewRef.current) {
        // Genera il PNG ad alta risoluzione
        const node = active === "brochure_social"
          ? staticPreviewRef.current.querySelector('[data-testid="static-brochure_social-slide-1"]')
          : staticPreviewRef.current.querySelector(`[data-testid="static-${active}"]`);
        if (!node) {
          toast.error("Anteprima non pronta. Riprova fra un attimo.");
          return;
        }
        const targetWidth = EXPORT_WIDTHS[active] || 1080;
        const html2canvas = (await import("html2canvas")).default;
        const rect = node.getBoundingClientRect();
        const scale = Math.max(1, targetWidth / Math.max(rect.width, 1));
        const canvas = await html2canvas(node, { scale, useCORS: true, allowTaint: true, backgroundColor: null, logging: false });
        const pngBlob = await new Promise((res) => canvas.toBlob((b) => res(b), "image/png", 1.0));
        const filename = nameFor(active, { ext: "png" });
        const file = new File([pngBlob], filename, { type: "image/png" });
        const intro = `Ciao! Ti mando in anteprima ${propTitle}.`;

        // Web Share API con file (mobile)
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: propTitle,
              text: intro,
            });
            toast.success("Condivisione aperta · scegli WhatsApp per inviare l'immagine");
            return;
          } catch (e) {
            if (e?.name === "AbortError") return; // utente ha chiuso il selettore
            // fallthrough al desktop fallback
          }
        }

        // Desktop fallback: auto-download + apri WhatsApp Web/app
        const url = URL.createObjectURL(pngBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        window.open(`https://wa.me/?text=${encodeURIComponent(intro)}`, "_blank", "noopener,noreferrer");

        // Primo utilizzo: toast lungo informativo. Successivi: toast breve.
        const TIP_KEY = "pronto_whatsapp_tip_seen_v1";
        const tipAlreadySeen = (() => {
          try { return localStorage.getItem(TIP_KEY) === "1"; } catch { return false; }
        })();
        if (tipAlreadySeen) {
          toast.success("Immagine scaricata · allega il PNG su WhatsApp", { duration: 4000 });
        } else {
          toast.success(
            "Immagine scaricata 📥\n\nSu desktop: clicca 📎 in WhatsApp Web e seleziona il PNG appena salvato (cartella Download).\nSu mobile: si allega da solo.",
            { duration: 11000, style: { maxWidth: 380, lineHeight: 1.5 } },
          );
          try { localStorage.setItem(TIP_KEY, "1"); } catch (_) { /* private mode */ }
        }
        return;
      }

      // ─── CASO POST / CAROSELLO (backend-rendered) ──────────────────
      // iter 313 r31 — bug fix: prima cadevano nel "FALLBACK formato
      // sconosciuto" che apriva wa.me solo con testo, SENZA immagine.
      // Ora scarichiamo il PNG dal backend e usiamo Web Share API (mobile)
      // o auto-download (desktop). Per carosello: condividiamo la SLIDE
      // ATTUALMENTE VISIBILE; le altre 4 si scaricano col "Scarica .zip".
      if ((active === "post" || active === "carousel") && (previewUrl || (carouselSlides && carouselSlides.length))) {
        const targetUrl = active === "carousel"
          ? (carouselSlides[carouselIdx]?.blobUrl || carouselSlides[0]?.blobUrl)
          : previewUrl;
        if (!targetUrl) {
          toast.error("Anteprima non pronta. Riprova fra un attimo.");
          return;
        }
        const pngBlob = await (await fetch(targetUrl)).blob();
        const filename = active === "carousel"
          ? nameFor(active, { ext: "png", idx: (carouselIdx + 1), suffix: `slide-${carouselIdx + 1}` })
          : nameFor(active, { ext: "png" });
        const file = new File([pngBlob], filename, { type: "image/png" });
        const intro = `Ciao! Ti mando in anteprima ${propTitle}.`;

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: propTitle, text: intro });
            toast.success("Condivisione aperta · scegli WhatsApp per inviare l'immagine");
            return;
          } catch (e) {
            if (e?.name === "AbortError") return;
          }
        }

        // Desktop fallback: auto-download + apri wa.me con testo
        const url = URL.createObjectURL(pngBlob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        window.open(`https://wa.me/?text=${encodeURIComponent(intro)}`, "_blank", "noopener,noreferrer");
        toast.success("Immagine scaricata · allega il PNG su WhatsApp", { duration: 6000 });
        return;
      }

      // ─── FALLBACK (formato sconosciuto) ────────────────────────────
      const text = `Ciao! Ti mando in anteprima ${propTitle}. Ti allego le foto qui sotto.`;
      // iter v29 r106 — Usa helper sicuro che gestisce popup bloccati
      // (Safari/iOS spesso li blocca, prima dicevamo "aperto" anche se no)
      const popup = window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
      if (!popup) {
        toast.error("WhatsApp non si è aperto", {
          description: "Il browser ha bloccato il popup. Riprova o copia il messaggio manualmente.",
        });
      } else {
        toast.success("WhatsApp aperto");
      }
    } catch (err) {
      console.error("WhatsApp share error:", err);
      toast.error("Impossibile preparare l'invio. Riprova fra poco.");
    } finally {
      setBusy(false);
    }
  };

  /** Scarica il "Pacchetto Social completo". */
  const onDownloadPack = async () => {
    if (packBusy) return;
    setPackBusy(true);
    toast.info("Sto preparando il pacchetto…", { duration: 3000 });
    try {
      const body = _buildPackBody();
      const startResp = await api.post(
        `/properties/${propertyId}/social/pack/start`,
        body,
      );
      const jobId = startResp?.data?.job_id;
      if (!jobId) throw new Error("Job non avviato");

      const POLL_MS = 1500;
      // iter v29 r215 — Pack ora include Reel + Story + Carosello (5 slide)
      // → tempo di generazione totale può salire a 90-120s. Alzo il timeout
      // polling da 80s a 180s per dare margine.
      const MAX_TRIES = Math.ceil(180000 / POLL_MS);
      let status = "queued";
      let errorMsg = "";
      for (let i = 0; i < MAX_TRIES; i++) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const { data } = await api.get(
          `/properties/${propertyId}/social/pack/${jobId}/status`,
        );
        status = data?.status;
        if (status === "done") break;
        if (status === "failed") {
          errorMsg = data?.error || "Generazione fallita";
          break;
        }
      }
      if (status !== "done") {
        throw new Error(errorMsg || "Timeout — riprova fra poco");
      }

      const resp = await api.get(
        `/properties/${propertyId}/social/pack/${jobId}/download`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pronto-pack-${propertyId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Pacchetto pronto!");
    } catch (err) {
      let detail = err?.message || "";
      try {
        const data = err?.response?.data;
        if (data && typeof data.text === "function") {
          const txt = await data.text();
          try {
            detail = JSON.parse(txt)?.detail || detail;
          } catch {
            // not JSON
          }
        } else if (data?.detail) {
          detail = data.detail;
        }
      } catch {
        // ignore
      }
      toast.error(detail || "Generazione pacchetto fallita");
    } finally {
      setPackBusy(false);
    }
  };

  /** Programma il pacchetto in background. */
  const onScheduleBackground = async () => {
    if (packBusy) return;
    const alreadyPending = activePackJobs.some(
      (j) =>
        j.property_id === propertyId &&
        (j.status === "queued" || j.status === "running"),
    );
    if (alreadyPending) {
      toast.info("Pacchetto già in lavorazione · attendi che finisca", {
        description: "Lo trovi nel vassoio in basso a destra.",
        duration: 4000,
      });
      return;
    }
    try {
      const body = _buildPackBody();
      await enqueuePackJob({
        property_id: propertyId,
        property_title: manifest?.property?.title || "",
        body,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Impossibile avviare il job");
    }
  };

  const onShare = async () => {
    setBusy(true);
    try {
      if (active === "carousel") {
        if (!carouselZip) {
          toast.error("Sto ancora preparando lo ZIP. Riprova fra un secondo.");
          return;
        }
        const blob = await (await fetch(carouselZip)).blob();
        const file = new File([blob], nameFor("carousel", { ext: "zip" }), { type: "application/zip" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: manifest?.property?.title || "Pronto", text: "" });
        } else {
          const a = document.createElement("a");
          a.href = carouselZip;
          a.download = file.name;
          a.click();
          toast.success("ZIP scaricato. Estrailo e carica le 5 slide su Instagram.");
        }
        return;
      }
      if (!previewUrl) return;
      const isVideo = active === "reel" || active === "before_after";
      const ext = isVideo ? "mp4" : "png";
      const mime = isVideo ? "video/mp4" : "image/png";
      const blob = await (await fetch(previewUrl)).blob();
      const file = new File([blob], nameFor(active, { ext }), { type: mime });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: manifest?.property?.title || "Pronto", text: "" });
      } else {
        const a = document.createElement("a");
        a.href = previewUrl;
        a.download = file.name;
        a.click();
        toast.success("Scaricato. Aprilo dall'app social per condividerlo.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Condivisione non disponibile. Prova a scaricarlo.");
      }
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (active === "carousel") {
      if (!carouselZip) {
        toast.error("Sto ancora preparando lo ZIP. Riprova fra un secondo.");
        return;
      }
      const a = document.createElement("a");
      a.href = carouselZip;
      a.download = nameFor("carousel", { ext: "zip" });
      a.click();
      return;
    }
    if (!previewUrl) return;
    // iter 313 r21 v8 — Mobile fix: `<a download>` con URL remoto non funziona
    // su iOS. Fetchamo il blob e usiamo Web Share API su mobile (Salva in Foto)
    // oppure download via blob URL su desktop.
    const isVideo = active === "reel" || active === "before_after";
    const ext = isVideo ? "mp4" : "png";
    const mime = isVideo ? "video/mp4" : "image/png";
    const filename = nameFor(active, { ext });
    try {
      const blob = await (await fetch(previewUrl)).blob();
      const file = new File([blob], filename, { type: mime });
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: manifest?.property?.title || "Pronto", text: "" });
        toast.success("Salva in Foto / File dal menù condividi");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        toast.success("Scaricato");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Download non riuscito");
    }
  };

  const onDownloadSlide = (slide) => {
    const a = document.createElement("a");
    a.href = slide.blobUrl;
    a.download = nameFor("carousel", { ext: "png", idx: slide.idx });
    a.click();
  };

  /**
   * iter v28r12 — Download del carosello come Reel video 9:16.
   * Backend renderizza le 5 slide PNG con cross-fade + bg blur in MP4 H.264.
   * Render lato server ~10-20s. UI: toast in attesa + nuova tab download.
   */
  const onDownloadCarouselAsReel = async () => {
    const tid = toast.loading("Sto creando il Reel dalle 5 slide… 10-20 sec");
    try {
      const url = `/properties/${propertyId}/social/carousel.mp4?template=${staticColor}&layout=${staticLayout}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "video/mp4" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = nameFor("carousel-reel", { ext: "mp4" });
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success("Reel pronto! Carica su Instagram come Reel.", { id: tid });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
      toast.error(`Render Reel fallito: ${detail}`, { id: tid });
    }
  };

  // ─── AI Auto-Selezione foto migliori (async + cache) ─────────────
  const _applyRanking = (data) => {
    if (!data?.cover) {
      toast("Analizzate le foto ma non ho trovato un candidato evidente.");
      return;
    }
    setAiSuggestion(data);
    if (manifest?.property?.photos) {
      const oldPhotos = manifest.property.photos;
      const allRankedIds = (data.ranked || []).map((s) => s.photo_id);
      const newPhotos = [...oldPhotos];
      const reordered = [];
      const usedIdx = new Set();
      const coverIdx = allRankedIds.indexOf(data.cover.photo_id);
      if (coverIdx >= 0 && coverIdx < newPhotos.length) {
        reordered.push(newPhotos[coverIdx]);
        usedIdx.add(coverIdx);
      }
      for (const g of (data.gallery || [])) {
        const idx = allRankedIds.indexOf(g.photo_id);
        if (idx >= 0 && idx < newPhotos.length && !usedIdx.has(idx)) {
          reordered.push(newPhotos[idx]);
          usedIdx.add(idx);
        }
      }
      newPhotos.forEach((p, i) => { if (!usedIdx.has(i)) reordered.push(p); });
      setManifest((m) => ({ ...m, property: { ...m.property, photos: reordered } }));
    }
    const cachedTag = data.from_cache ? " (cache)" : "";
    toast.success(`Cover scelta: ${data.cover.room_type} · score ${Math.round(data.cover.score)}/100${cachedTag}`);
  };

  const onAiSelectPhotos = async ({ force = false } = {}) => {
    setAiSelecting(true);
    if (force) setAiSuggestion(null);
    try {
      const startResp = await api.post(
        `/properties/${propertyId}/social/auto-select-photos${force ? "?force=true" : ""}`,
      );
      if (startResp.data?.status === "done" || startResp.data?.from_cache) {
        _applyRanking(startResp.data);
        return;
      }
      const MAX_ATTEMPTS = 30;
      const POLL_MS = 3000;
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        try {
          const pollResp = await api.get(`/properties/${propertyId}/social/photo-ranking`);
          const status = pollResp.data?.status;
          if (status === "done") {
            _applyRanking(pollResp.data);
            return;
          }
          if (status === "failed") {
            toast.error("Analisi fallita. Riprova fra qualche secondo.");
            return;
          }
          if (status === "stale") {
            toast("Le foto sono cambiate, riavvio l'analisi…");
            const restart = await api.post(`/properties/${propertyId}/social/auto-select-photos`);
            if (restart.data?.status === "done") {
              _applyRanking(restart.data);
              return;
            }
          }
        } catch (pollErr) {
          console.warn("polling error:", pollErr);
        }
      }
      toast.error("Analisi ha superato i 90 secondi. Riprova più tardi.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) toast.error("Selezione automatica foto è una feature Pro");
      else if (status === 404) toast.error(detail || "Carica almeno una foto");
      else toast.error("Errore durante la selezione");
    } finally {
      setAiSelecting(false);
    }
  };

  // ─── Export client-side dei template statici ──────────────────────
  const onStaticDownload = async () => {
    if (!staticPreviewRef.current) {
      toast.error("Anteprima non pronta. Riprova fra un attimo.");
      return;
    }
    // iter v27 r11 — Se la brochure è già pronta (iOS · pre-generata),
    // apriamo subito lo share-sheet con gesture utente FRESCO.
    if (active === "brochure" && brochurePdfReady) {
      await smartDownloadBlob(brochurePdfReady.blob, brochurePdfReady.filename, "application/pdf");
      if (!isIOSDevice()) toast.success("PDF stampabile scaricato.");
      return;
    }
    setExporting(true);
    try {
      if (active === "brochure") {
        // iter v28 r17 — Routato sul motore VECTOR (jsPDF) usato dal
        // BrochureExportModal. Stop a html2canvas: niente più foto sgranate,
        // niente più "LA RESIDENZA" hardcoded, niente più logo schiacciato.
        // Le foto vanno via `addPhotoCover` (300 DPI, no distorsione) e i
        // testi sono vettoriali (zoom infinito, stampa nitida).
        // Stessa pipeline di BrochureExportModal: manifest.property + agency
        // → resolveManifest* (URL firmati con auth token) → buildData.
        if (!manifest?.property) {
          toast.error("PDF stampabile non pronto. Riprova fra un attimo.");
          return;
        }
        const property = resolveManifestProperty(manifest.property);
        const agency = resolveManifestAgency(manifest?.agency);
        const data = buildData(property, agency || {});
        const lay = LAYOUTS[staticLayout];
        const col = COLORS[staticColor];
        const showWatermark = agency?.plan !== "pro";
        const fname = nameFor("brochure", { ext: "pdf", layout: staticLayout });
        const pdfBlob = await generateVectorBrochure({
          data, lay, col,
          showWatermark,
          showPrice: true,
          layoutKey: staticLayout,
        });
        if (isIOSDevice()) {
          setBrochurePdfReady({ blob: pdfBlob, filename: fname });
        } else {
          await smartDownloadBlob(pdfBlob, fname, "application/pdf");
          toast.success("PDF stampabile scaricato.");
        }
        archiveAsset(pdfBlob, {
          kind: "brochure",
          format: "pdf",
          filename: fname,
          propertyId,
          label: manifest?.property?.title || null,
          layout: staticLayout,
          colorPalette: staticColor,
        });
      } else if (active === "brochure_social") {
        // Carosello Instagram 4 slide 1080×1350 (4:5) → ZIP per upload diretto
        const slideNodes = [1, 2, 3, 4]
          .map((idx) => staticPreviewRef.current.querySelector(`[data-testid="static-brochure_social-slide-${idx}"]`))
          .filter(Boolean);
        if (slideNodes.length !== 4) {
          toast.error("Impossibile esportare il carosello. Riprova.");
          return;
        }
        const fname = nameFor("brochure_social", { ext: "zip", layout: staticLayout });
        const zipPrefix = makeAssetFilename({
          propertyTitle: manifest?.property?.title,
          propertyCity: manifest?.property?.city,
          kind: "brochure_social",
          layout: staticLayout,
          ext: "", // verrà ignorato, prefix solo
        }).replace(/\.$/, "");
        const zipBlob = await exportNodesAsZip({
          nodes: slideNodes,
          targetWidth: 1080,
          filename: fname,
          prefix: zipPrefix,
        });
        toast.success("Carosello scaricato. Estrai lo ZIP e carica le 4 slide su Instagram.");
        archiveAsset(zipBlob, {
          kind: "carousel",
          format: "zip",
          filename: fname,
          propertyId,
          label: `Scheda social · ${manifest?.property?.title || ""}`.trim(),
          layout: staticLayout,
          colorPalette: staticColor,
        });
      } else {
        const node = staticPreviewRef.current.querySelector(`[data-testid="static-${active}"]`);
        if (!node) {
          toast.error("Anteprima non trovata.");
          return;
        }
        const targetWidth = EXPORT_WIDTHS[active] || 1080;
        const fname = nameFor(active, { ext: "png", layout: staticLayout });
        const pngBlob = await exportStaticAsPng({ node, targetWidth, filename: fname });
        toast.success("Immagine scaricata.");
        archiveAsset(pngBlob, {
          kind: classifyKind(active),
          format: "png",
          filename: fname,
          propertyId,
          label: labelForFormat(active, manifest),
          layout: staticLayout,
          colorPalette: staticColor,
        });
      }
    } catch (err) {
      console.error("Static export error:", err);
      toast.error("Export fallito. Riprova.");
    } finally {
      setExporting(false);
    }
  };

  const onStaticShare = async () => {
    if (!staticPreviewRef.current) return;
    setExporting(true);
    try {
      if (active === "brochure" || active === "brochure_social") {
        await onStaticDownload();
        return;
      }
      const node = staticPreviewRef.current.querySelector(`[data-testid="static-${active}"]`);
      if (!node) return;
      const targetWidth = EXPORT_WIDTHS[active] || 1080;
      const html2canvas = (await import("html2canvas")).default;
      const rect = node.getBoundingClientRect();
      const scale = Math.max(1, targetWidth / Math.max(rect.width, 1));
      const canvas = await html2canvas(node, { scale, useCORS: true, allowTaint: true, backgroundColor: null, logging: false });
      const pngBlob = await new Promise((res) => canvas.toBlob((b) => res(b), "image/png", 1.0));
      const file = new File([pngBlob], nameFor(active, { ext: "png", layout: staticLayout }), { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: manifest?.property?.title || "Pronto" });
      } else {
        const url = URL.createObjectURL(pngBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Scaricato. Aprilo dall'app social per condividerlo.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Static share error:", err);
        toast.error("Condivisione non disponibile. Prova a scaricarlo.");
      }
    } finally {
      setExporting(false);
    }
  };

  if (!manifest) {
    if (manifestError) {
      return (
        <div className="mt-8 bg-white border border-[#EAE7DF] rounded-xl p-8 text-center" data-testid="social-studio-error">
          <div className="text-[14px] font-semibold text-[#1C1C1A] mb-2">
            Editor social non disponibile
          </div>
          <div className="text-[12px] text-[#7C7B73] mb-4 leading-relaxed">
            {manifestError}
          </div>
          <button
            onClick={fetchManifest}
            className="px-4 py-2 bg-[#1C1C1A] text-white rounded text-[12px] font-semibold hover:bg-[#2D2D29] transition-colors"
            data-testid="social-studio-retry"
          >
            Riprova
          </button>
        </div>
      );
    }
    return (
      <div className="mt-8 bg-white border border-[#EAE7DF] rounded-xl p-8 text-center" data-testid="social-studio-loading">
        <BrandSpinner size={56} label="Carico Editor social" />
      </div>
    );
  }

  const photoCount = manifest.photo_count || 0;
  // iter 312 r5 — Pro paganti = niente watermark (white label).
  // canTryStyle = SEMPRE TRUE durante il periodo beta (l'utente vuole
  // poter provare tutti i 32 layout × colore senza lock visivi). Il
  // watermark Pronto resta sui materiali esportati per i non-Pro.
  const isPro = manifest.plan === "pro";
  // iter v28 r185 — User M1105: 3 tier per colori (free=3, base=5, pro=13).
  const userPlan = manifest.plan || "free";
  const canTryStyle = true;

  const formats = [
    // ── Quotidiano: contenuti regolari per nutrire il feed ───────
    { key: "post", label: "Post", sublabel: "1:1 Instagram / Facebook", icon: ImageSquare, ratio: "aspect-square", group: "quotidiano" },
    { key: "carousel", label: "Carosello", sublabel: "5 slide IG · 1:1", icon: Stack, ratio: "aspect-square", group: "quotidiano" },
    { key: "story", label: "Story", sublabel: "9:16 WhatsApp / IG", icon: Sparkle, ratio: "aspect-[9/16]", group: "quotidiano" },
    { key: "reel", label: "Reel", sublabel: "9:16 video 17s", icon: FilmSlate, ratio: "aspect-[9/16]", group: "quotidiano" },
    { key: "before_after", label: "Prima / Dopo", sublabel: "9:16 wipe video 5s", icon: ArrowsClockwiseDup, ratio: "aspect-[9/16]", group: "quotidiano" },
    // ── Per occasione: Open House + Venduto + Prezzo nuovo (iter v28 r98) ──
    // Riusano Post 1:1 / Story 9:16 standard + badge. Dati editabili.
    { key: "occ_open_house",   label: "Open House",   sublabel: "Post o Story · porte aperte",       icon: Calendar,    ratio: "aspect-square", isNew: true, group: "occasione" },
    { key: "occ_venduto",      label: "Venduto",      sublabel: "Post o Story · social proof",       icon: CheckCircle, ratio: "aspect-square", isNew: true, group: "occasione" },
    { key: "occ_prezzo_nuovo", label: "Prezzo nuovo", sublabel: "Post o Story · ribasso annunciato", icon: CurrencyEur, ratio: "aspect-square", isNew: true, group: "occasione" },
    // iter v28 r98 — Brochure A4 ELIMINATA definitivamente.
  ];

  // iter v28 r454 — User M1370: «Elimina i contenuti Per occasione degli
  // annunci affitti in editor social anche in annuncio». Open House /
  // Venduto / Prezzo nuovo hanno senso SOLO sulla vendita. Sull'affitto
  // li nascondiamo dal selettore.
  const _listingType = (manifest?.property?.listing_type || "").toLowerCase();
  const _isAffitto = _listingType === "affitto";
  const visibleFormats = _isAffitto
    ? formats.filter((f) => f.group !== "occasione")
    : formats;

  const isStatic = STATIC_FORMATS.includes(active);
  let activeFormat = formats.find((f) => f.key === active);
  // iter 313 r21 v19 — Per occasione: ratio dinamico in base a Post/Story
  if (activeFormat && (active === "occ_open_house" || active === "occ_venduto" || active === "occ_prezzo_nuovo")) {
    activeFormat = {
      ...activeFormat,
      ratio: occasionFormat === "9x16" ? "aspect-[9/16]" : "aspect-square",
    };
  }
  const isCarousel = active === "carousel";
  // Il nuovo StaticStyleSelector è ora UNICO selettore di stile per tutti i formati.
  const showStaticStyleSelector = true;

  const goPrev = () => setCarouselIdx((i) => Math.max(0, i - 1));
  const goNext = () => setCarouselIdx((i) => Math.min(carouselSlides.length - 1, i + 1));

  return (
    <>
      {/* iter v33 r18 · Blocco preventivo. Se l'enhance foto è ancora
          in corso, avviso l'agente in cima e disabilito le azioni
          Genera. Così non arriva mai al 503 di ffmpeg. */}
      {enhanceBusy && (
        <div
          className="mb-4 px-4 py-3 rounded-md bg-[#EEF3EA] border border-[#B7C8A3] text-[#3F5230] flex items-start gap-3"
          data-testid="social-enhance-in-progress-banner"
        >
          <Sparkle size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
          <div className="text-[12.5px] leading-snug">
            <div className="font-semibold">Sto ancora migliorando le foto ({enhanceStatus.done}/{enhanceStatus.total || "?"})</div>
            <div className="text-[#5A6E4C]">
              I contenuti social saranno pronti tra poco. Aspetta che finisca — così vengono più belli e non rischi errori.
            </div>
          </div>
        </div>
      )}

      {/* iter 275 round 51: hint discreto quando lavori su una scheda
          "Senza annuncio" (creata dal flusso Galleria → contenuti). */}
      {manifest?.property?.is_ad_hoc && (
        <div
          className="mb-4 px-4 py-2.5 rounded-md bg-[#FFFAEC] border border-[#E8D9A8] text-[12.5px] text-[#7A6212] flex flex-wrap items-center gap-x-2 gap-y-1"
          data-testid="social-studio-adhoc-hint"
        >
          <span className="font-semibold">Scheda senza annuncio.</span>
          <span>I contenuti che generi qui sono pronti subito.</span>
          <a
            href="/dashboard/annunci/nuovo"
            className="ml-auto underline underline-offset-2 font-semibold hover:text-[#5A4400]"
            data-testid="social-studio-adhoc-cta"
          >
            Quando vuoi farne un annuncio vero, parti da qui →
          </a>
        </div>
      )}

      {/* iter 312 r6 — rimosso QuickGuide hero (4 grandi card) perché
          con il nuovo layout 2 colonne l'utente vede subito tutti i
          formati nel selettore + anteprima sempre a destra. Era
          ridondante e occupava troppa altezza prima dell'azione vera. */}

      {/* iter v33 r9 · Coachmark uniforme "Cosa puoi fare qui" — Social è
          step 4 (Promuovi): amplifica l'annuncio già pubblicato. */}
      <div className="max-w-6xl mx-auto">
        <SectionCoachmark
          id="property-social"
          title="Post pronti per Instagram e WhatsApp"
          steps={[
            {
              icon: <ImageSquare size={15} weight="bold" />,
              title: "Scegli il formato",
              body: "Post quadrato, Story verticale, Reel video o Prima/Dopo del virtual staging.",
            },
            {
              icon: <Sparkle size={15} weight="bold" />,
              title: "Anteprima già pronta",
              body: "Foto, testo e colore brand montati in automatico. Niente Photoshop, niente ritagli.",
            },
            {
              icon: <DownloadSimple size={15} weight="bold" />,
              title: "Scarica o condividi",
              body: "PNG per il feed, MP4 per i reel, link WhatsApp per mandarlo a un cliente.",
            },
          ]}
        />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 max-w-6xl mx-auto" data-testid="social-studio">
        {/* iter 312 r6 — anteprima SEMPRE visibile a destra (sticky su desktop)
            in modo che l'utente NON debba scorrere giù dopo aver cliccato un
            template. Su mobile resta a colonna unica con preview in cima. */}
        {/* Left col: format selector + plan badge */}
        <div className="order-2 lg:order-1 min-w-0">
          <div className="bg-white border border-[#EAE7DF] rounded-xl p-5">
            <DismissibleIntro storageKey="social:editor-intro" className="mb-3">
              <div className="pr-9">
                <div className="flex items-center gap-2 mb-1">
                  <span className="overline">Editor social</span>
                  {isPro && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300/50 text-[9.5px] uppercase tracking-[0.18em] font-semibold text-amber-800">
                      <Crown size={10} weight="fill" /> Pro
                    </span>
                  )}
                </div>
                <h3 className="font-editorial text-[20px] sm:text-[22px] text-[#1C1C1A] mb-1 leading-tight">
                  Crea post e<br />
                  <span className="italic-accent text-[#7C8B6E]">stories.</span>
                </h3>
                <p className="text-[12.5px] text-[#6B6A65] leading-relaxed">
                  Scegli il formato e la foto. L&apos;anteprima qui sopra (mobile) o a destra (desktop) si aggiorna in tempo reale.
                </p>
              </div>
            </DismissibleIntro>
            {photoCount === 0 && (
              <div className="mb-4 px-3 py-2.5 bg-amber-50/80 border border-amber-200 rounded-md text-[11.5px] text-amber-900 leading-snug" data-testid="zero-photo-hint">
                <strong className="font-semibold">Nessuna foto caricata.</strong> Le anteprime usano foto di esempio. Carica le tue foto per vedere il risultato vero.
              </div>
            )}

            {/* iter 312 r7 — ORDINE PULITO (richiesta utente):
                1. Formati (Per occasione + Quotidiano) — selettore principale
                2. Layout + Colori — applica a tutto
                3. Pacchetto Social completo — scorciatoia "fammi tutto"
                Rimossi: "Come funziona" (rumore), label "↓ Inizia qui". */}

            <div id="social-format-selector" data-testid="social-format-anchor">
              <FormatSelector
                formats={visibleFormats}
                active={active}
                manifest={manifest}
                photoCount={photoCount}
                onSelect={setActive}
              />
            </div>

            {/* iter v28 r98 — Pannello "Per occasione" (Open House / Venduto / Prezzo nuovo) */}
            {(active === "occ_open_house" || active === "occ_venduto" || active === "occ_prezzo_nuovo") && (
              <div
                className="mt-4 p-3 rounded-md border border-[#EAE7DF] bg-[#FAFAF7] space-y-3"
                data-testid="occasione-panel"
              >
                <div>
                  <div className="text-[12px] font-semibold text-[#1C1C1A] mb-1.5">Formato</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "1x1", label: "Post 1:1" },
                      { v: "9x16", label: "Story 9:16" },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setOccasionFormat(opt.v)}
                        className={`px-3 py-2 rounded-md border text-[12.5px] font-semibold transition-colors ${
                          occasionFormat === opt.v
                            ? "border-[#7C8B6E] bg-[#F1F4ED] text-[#1C1C1A]"
                            : "border-[#EAE7DF] bg-white text-[#6B6A65] hover:border-[#C8CDB8]"
                        }`}
                        data-testid={`occ-fmt-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {active === "occ_open_house" ? (
                  <div className="grid grid-cols-3 gap-2">
                    <label className="block">
                      <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Data evento</span>
                      <input
                        type="date"
                        value={occasionDate}
                        onChange={(e) => setOccasionDate(e.target.value)}
                        className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                        data-testid="occ-date"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Inizio</span>
                      <input
                        type="time"
                        value={occasionTime}
                        onChange={(e) => setOccasionTime(e.target.value)}
                        className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                        data-testid="occ-time"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Fine</span>
                      <input
                        type="time"
                        value={occasionTimeEnd}
                        onChange={(e) => setOccasionTimeEnd(e.target.value)}
                        className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                        data-testid="occ-time-end"
                      />
                    </label>
                  </div>
                ) : active === "occ_venduto" ? (
                  <label className="block">
                    <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Giorni dalla pubblicazione al rogito</span>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={occasionDays}
                      onChange={(e) => setOccasionDays(e.target.value)}
                      className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                      data-testid="occ-days"
                    />
                  </label>
                ) : (
                  // iter v28 r98 — Prezzo nuovo
                  // iter v28 r165 — User M1082: se l'annuncio è in "trattativa
                  // riservata", non si può applicare il nuovo prezzo. Mostriamo
                  // un avviso bloccante invece dell'input.
                  manifest?.property?.hide_price ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900 leading-snug" data-testid="occ-price-locked">
                      <strong className="block text-[12px]">Trattativa riservata attiva</strong>
                      Per pubblicare il "nuovo prezzo" devi prima disattivare la trattativa riservata dall'annuncio e impostare un prezzo evidente.
                    </div>
                  ) : (
                    <label className="block">
                      <span className="text-[11px] font-semibold text-[#1C1C1A] block mb-1">Nuovo prezzo · €</span>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        placeholder="Es. 280000"
                        value={occasionNewPrice}
                        onChange={(e) => setOccasionNewPrice(e.target.value)}
                        className="w-full px-2.5 py-2 rounded-md border border-[#EAE7DF] bg-white text-[12.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E]"
                        data-testid="occ-new-price"
                      />
                    </label>
                  )
                )}

                <p className="text-[10.5px] text-[#9A968B] leading-snug">
                  {active === "occ_open_house"
                    ? "Stesso layout del Post / Story con foto leggermente più scura e badge OPEN HOUSE."
                    : active === "occ_venduto"
                      ? "Stesso layout del Post / Story con badge VENDUTO IN N GIORNI per celebrare la chiusura."
                      : "Stesso layout del Post / Story con badge PREZZO NUOVO per annunciare un ribasso."}
                </p>
              </div>
            )}

            {/* Layout + Colore selector — UNICO selettore di stile */}
            {showStaticStyleSelector && (
              <StaticStyleSelector
                isPro={canTryStyle}
                userPlan={userPlan}
                layoutOptions={STATIC_LAYOUT_OPTIONS}
                colorOptions={STATIC_COLOR_OPTIONS}
                layoutMini={LAYOUT_MINI}
                staticLayout={staticLayout}
                setStaticLayout={setStaticLayout}
                staticColor={staticColor}
                setStaticColor={setStaticColor}
                styleSyncing={styleSyncing}
                stylePulse={stylePulse}
              />
            )}

            {/* iter v28 r146 — User M1063: «invertimi testo del post con
                pacchetto social completo». A SINISTRA va "Testo del post"
                (CaptionGenerator). A DESTRA va "Pacchetto social completo"
                (PackBox) + "Seleziona automaticamente le foto migliori". */}
            {!isStatic && photoCount > 0 && (
              <div className="mt-5">
                <CaptionGenerator
                  propertyId={propertyId}
                  occasion={
                    active === "occ_open_house"
                      ? "open_house"
                      : active === "occ_venduto"
                      ? "venduto"
                      : "nuovo_annuncio"
                  }
                  eventDate={
                    active === "occ_open_house" && occasionDate
                      ? (() => {
                          const [, mm, dd] = occasionDate.split("-");
                          return dd && mm ? `${dd}/${mm}` : "";
                        })()
                      : ""
                  }
                  eventTime={active === "occ_open_house" ? occasionTime : ""}
                  eventTimeEnd={active === "occ_open_house" ? occasionTimeEnd : ""}
                  daysToSell={active === "occ_venduto" ? occasionDays : null}
                  tone={captionTone}
                  setTone={setCaptionTone}
                />
              </div>
            )}

            {!isPro && (
              <div className="mt-5 p-3 rounded-md bg-amber-50/60 border border-amber-300/40">
                <div className="flex items-center gap-1.5 mb-1">
                  <Lock size={12} weight="fill" className="text-amber-700" />
                  <span className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-amber-800">Watermark Pronto presente</span>
                </div>
                <p className="text-[11.5px] text-amber-900/85 leading-snug">
                  Passa al piano <strong>Pro</strong> per rimuoverlo e ottenere contenuti completamente privi del nostro logo.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right col: live preview — sticky su desktop, sopra su mobile */}
        <div className="order-1 lg:order-2 min-w-0">
          <div className="lg:sticky lg:top-4 bg-white border border-[#EAE7DF] rounded-xl p-5 sm:p-6" data-testid="social-preview-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">Anteprima</div>
                <div className="font-editorial text-[18px] text-[#1C1C1A] leading-tight mt-0.5">
                  {activeFormat?.label}
                  {isCarousel && carouselSlides.length > 0 && (
                    <span className="ml-2 text-[12px] text-[#9A968B] font-sans">
                      slide {carouselIdx + 1} / {carouselSlides.length}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  // iter 275: formati statici sono renderizzati client-side
                  // (StaticTemplatePreview). Chiamare fetchAsset() qui scatena
                  // un 404 sul backend (`/social/reel.mp4` fallback) → toast
                  // "Generazione fallita" + crash overlay. Per i statici
                  // diamo solo un feedback visivo (pulse).
                  if (isStatic) {
                    setStylePulse(Date.now());
                    toast.success("Anteprima aggiornata");
                    return;
                  }
                  fetchAsset(active);
                }}
                disabled={loading}
                className="text-[11px] uppercase tracking-[0.18em] text-[#7C8B6E] hover:text-[#1C1C1A] flex items-center gap-1 font-semibold"
                data-testid="social-refresh"
              >
                <ArrowsClockwise size={11} weight="bold" /> Rigenera
              </button>
            </div>

            {/* iter r95 · Rimosso `reel-progress-banner` esterno duplicato.
                Il progresso (elapsed + eta + progress bar + "puoi chiudere
                l'app") è ora DENTRO il PreviewBody sotto forma di loader
                unico, come richiesto dall'utente. */}

            <PreviewBody
              active={active}
              isStatic={isStatic}
              isCarousel={isCarousel}
              activeFormat={activeFormat}
              manifest={manifest}
              isPro={isPro}
              staticLayout={staticLayout}
              staticColor={staticColor}
              staticPreviewRef={staticPreviewRef}
              loading={loading}
              previewUrl={previewUrl}
              previewError={previewError}
              onRetryPreview={() => { setPreviewError(null); fetchAsset(active); }}
              carouselSlides={carouselSlides}
              carouselIdx={carouselIdx}
              setCarouselIdx={setCarouselIdx}
              goPrev={goPrev}
              goNext={goNext}
              reelAspect={reelAspect}
              setReelAspect={setReelAspect}
              postAspect={postAspect}
              setPostAspect={setPostAspect}
              baAspect={baAspect}
              setBaAspect={setBaAspect}
              reelShowPrice={showPrice}
              setReelShowPrice={setShowPrice}
              showPrice={showPrice}
              baLabelKind={baLabelKind}
              setBaLabelKind={setBaLabelKind}
              baPhotoId={baPhotoId}
              setBaPhotoId={setBaPhotoId}
              baPhotoIds={baPhotoIds}
              setBaPhotoIds={setBaPhotoIds}
              heroPhotoId={heroPhotoId}
              setHeroPhotoId={setHeroPhotoId}
              reelJobProgress={active === "reel" && loading ? reelJobProgress : null}
            />

            {/* iter r95e · Fix 3 · Pulsante "Rigenera Reel" · appare SOLO
                dopo che il Reel corrente è stato caricato correttamente
                (previewUrl set, non in loading). Bypassa tutti i layer di
                cache (RAM + filesystem + object storage) e forza una
                nuova generazione con nuovo addebito 5 crediti.
                Il backend gestisce l'idempotency window 60s: doppio tap
                o retry rete NON producono doppio addebito. */}
            {active === "reel" && previewUrl && !loading && (
              <div className="mt-2 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const ok = window.confirm(
                      "Rigenerare il Reel?\n\nVerrà creato un nuovo video (5 crediti). "
                      + "Il Reel attuale verrà sostituito."
                    );
                    if (ok) fetchAsset("reel", { forceRegen: true });
                  }}
                  disabled={busy || exporting}
                  className="text-[12px] px-3 py-1.5 rounded-md bg-white border border-[#D9D3C4] text-[#1C1C1A] hover:bg-[#F5F1E8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reel-regenerate-btn"
                >
                  Rigenera Reel
                </button>
              </div>
            )}

            <SocialActions
              active={active}
              isStatic={isStatic}
              isCarousel={isCarousel}
              exporting={exporting}
              busy={busy}
              previewUrl={previewUrl}
              carouselSlides={carouselSlides}
              shares={shares}
              carouselIdx={carouselIdx}
              onShare={onShare}
              onStaticShare={onStaticShare}
              onDownload={onDownload}
              onStaticDownload={onStaticDownload}
              onWhatsAppShare={onWhatsAppShare}
              onCreatePublicLink={onCreatePublicLink}
              onDownloadSlide={onDownloadSlide}
              onDownloadAsReel={onDownloadCarouselAsReel}
              onCopyShareUrl={copyShareUrl}
              onRevokeShare={onRevokeShare}
              brochurePdfReady={brochurePdfReady}
              propertyId={propertyId}
              captionOccasion={
                active === "occ_open_house"
                  ? "open_house"
                  : active === "occ_venduto"
                  ? "venduto"
                  : "nuovo_annuncio"
              }
              captionEventDate={
                active === "occ_open_house" && occasionDate
                  ? (() => {
                      const [, mm, dd] = occasionDate.split("-");
                      return dd && mm ? `${dd}/${mm}` : "";
                    })()
                  : ""
              }
              captionEventTime={active === "occ_open_house" ? occasionTime : ""}
              captionEventTimeEnd={active === "occ_open_house" ? occasionTimeEnd : ""}
              captionDaysToSell={active === "occ_venduto" ? occasionDays : null}
              captionTone={captionTone}
              setCaptionTone={setCaptionTone}
            />

            {/* iter v28 r176 — User M1095: «su desktop rivoglio PackBox e AI
                photo dove erano prima, a destra sotto l'anteprima». Versione
                desktop-only (lg:block). La copia mobile-only vive fuori dal
                grid principale e ha `lg:hidden`. */}
            {photoCount > 0 && (
              <div className="hidden lg:block mt-5 space-y-3" data-testid="pack-desktop-section">
                <button
                  onClick={() => onAiSelectPhotos()}
                  disabled={!isPro || aiSelecting || !manifest?.photo_count}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-[#1C1C1A] text-[#FAFAF7] text-[13px] font-semibold transition-colors hover:bg-[#2A2A28] disabled:opacity-50"
                  data-testid="ai-select-photos-btn-desktop"
                  title={!isPro ? "Disponibile con il piano Pro" : ""}
                >
                  <Sparkle size={15} weight="fill" className="text-[#B8C4A6]" />
                  {aiSelecting ? "Sto analizzando le foto…" : "Seleziona automaticamente le foto migliori"}
                  {!isPro && <Lock size={12} weight="fill" className="text-amber-400" />}
                </button>
                {aiSuggestion?.cover && (
                  <div className="text-[10px] text-[#6B6A65] leading-snug bg-[#F1F4ED]/60 border border-[#E5E7E0] rounded-md px-2.5 py-1.5">
                    <strong className="text-[#1C1C1A]">Cover scelta</strong> · {aiSuggestion.cover.room_type} · score {Math.round(aiSuggestion.cover.score)}/100
                  </div>
                )}
                <PackBox
                  packBusy={packBusy}
                  captionTone={captionTone}
                  setCaptionTone={setCaptionTone}
                  captionVariants={captionVariants}
                  setCaptionVariants={setCaptionVariants}
                  onDownloadPack={onDownloadPack}
                  onScheduleBackground={onScheduleBackground}
                />
              </div>
            )}
          </div>
        </div>

        {/* iter v28 r173 — User M1090: «da mobile metti AI photo + PackBox
            in fondo». Versione mobile-only (lg:hidden): full-width sotto
            le due colonne così è chiaro e visibile dopo aver visto tutto.
            iter v28 r174 — order-last per restare ultimo nel grid mobile.
            iter v28 r176 — `lg:hidden` perché su desktop questa sezione vive
            DENTRO la colonna destra (vedi sopra). */}
        {photoCount > 0 && (
          <div
            className="lg:hidden order-last mt-6 max-w-6xl mx-auto space-y-3"
            data-testid="pack-bottom-section"
          >
            <button
              onClick={() => onAiSelectPhotos()}
              disabled={!isPro || aiSelecting || !manifest?.photo_count}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-[#1C1C1A] text-[#FAFAF7] text-[13px] font-semibold transition-colors hover:bg-[#2A2A28] disabled:opacity-50"
              data-testid="ai-select-photos-btn-bottom"
              title={!isPro ? "Disponibile con il piano Pro" : ""}
            >
              <Sparkle size={15} weight="fill" className="text-[#B8C4A6]" />
              {aiSelecting ? "Sto analizzando le foto…" : "Seleziona automaticamente le foto migliori"}
              {!isPro && <Lock size={12} weight="fill" className="text-amber-400" />}
            </button>
            {aiSuggestion?.cover && (
              <div className="text-[10px] text-[#6B6A65] leading-snug bg-[#F1F4ED]/60 border border-[#E5E7E0] rounded-md px-2.5 py-1.5">
                <strong className="text-[#1C1C1A]">Cover scelta</strong> · {aiSuggestion.cover.room_type} · score {Math.round(aiSuggestion.cover.score)}/100
              </div>
            )}
            <PackBox
              packBusy={packBusy}
              captionTone={captionTone}
              setCaptionTone={setCaptionTone}
              captionVariants={captionVariants}
              setCaptionVariants={setCaptionVariants}
              onDownloadPack={onDownloadPack}
              onScheduleBackground={onScheduleBackground}
            />
          </div>
        )}
      </div>
    </>
  );
}
